/**
 * Admin — « Utilisateurs » (`/tutor/admin/users`, migration 0306).
 *
 * Liste des comptes (recherche nom / email, filtre par rôle, accès en cours)
 * et fiche d'un compte : accès actuels (abonnement, pass, deck SKILLCARDS),
 * OFFRIR un pass gratuit (accès complet / épreuves, durée au choix, prolonge
 * un pass actif) avec une notification push + email au message prédéfini et
 * modifiable, retrait d'un pass offert, historique des cadeaux, et
 * suppression définitive d'un compte interne (`@jeraisonne.fr`) ou de test.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Gift, LogIn, Search, Trash2, Users, X } from 'lucide-react';
import { AvatarCircle, Button, Paginator, cn } from '@jeraisonne/ui';
import { usePagedList } from '@/lib/use-paged-list';
import { useAuth } from '@/lib/auth';
import { deleteBlockLabel, deleteBlockReason } from '@/lib/admin/user-delete';
import { adminSwitchBlockReason, switchToInternalAccount } from '@/lib/admin/admin-switch';
import {
  DEFAULT_GIFT_TEMPLATE,
  DEFAULT_GIFT_TITLE,
  ADMIN_USER_SORTS,
  GIFT_ACCESS_OPTIONS,
  GIFT_BODY_MAX,
  GIFT_DURATION_PRESETS,
  GIFT_TITLE_MAX,
  PASS_SOURCE_LABELS,
  ROLE_LABELS,
  formatGiftDate,
  formatLastActivity,
  giftAccessLabel,
  giftDurationLabel,
  giftEndDate,
  giftFirstName,
  parseGiftDays,
  renderGiftMessage,
  userAccessBadges,
  type AccessBadge,
  type AdminUserRow,
  type AdminUserSort,
  type GiftAccessType,
} from '@/lib/admin/user-gifts';
import { PendingAccountsSection } from '@/components/admin/pending-accounts-section';
import { UserActivitySection } from '@/components/admin/user-activity-section';
import { UserPermissionsSection } from '@/components/admin/user-permissions-section';
import {
  adminAccessErrorMessage,
  useAdminUserAccess,
  useAdminUsers,
  useDeleteUser,
  useGrantAccess,
  useRevokeGift,
  type GrantAccessResult,
} from '@/lib/admin/users';

const inputCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 w-full min-h-[2.75rem] rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2 ocean:bg-white/10';
const labelCls = 'font-display text-ink-700 text-xs font-semibold';

const ROLE_FILTERS: { value: string | null; label: string }[] = [
  { value: null, label: 'Tous' },
  { value: 'student', label: 'Élèves' },
  { value: 'parent', label: 'Parents' },
  { value: 'tutor', label: 'Professeurs' },
  { value: 'player', label: 'Curieux' },
  { value: 'admin', label: 'Admins' },
];

const BADGE_TONES: Record<AccessBadge['tone'], string> = {
  success: 'bg-success-50 text-success-800',
  primary: 'bg-primary-50 text-primary-700',
  accent: 'bg-accent-50 text-accent-800',
  neutral: 'bg-ink-100 text-ink-700',
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

export function TutorAdminUsersPage() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [sort, setSort] = useState<AdminUserSort>('activity');
  const [openUser, setOpenUser] = useState<AdminUserRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const users = useAdminUsers({ query, role, sort });
  const { items, paginator } = usePagedList(users.data, {
    resetKeys: [query, role, sort],
    unit: 'comptes',
  });

  return (
    <div className="mx-auto max-w-4xl space-y-5 px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div>
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
          <Users className="h-6 w-6" aria-hidden /> Utilisateurs
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Consulte les comptes et leurs accès, offre un pass gratuit avec un message.
        </p>
      </div>

      <div className="space-y-3">
        <label className="relative block">
          <span className="sr-only">Rechercher un compte</span>
          <Search
            className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            aria-hidden
          />
          <input
            type="search"
            className={cn(inputCls, 'pl-9')}
            placeholder="Nom, email ou identifiant"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className={labelCls}>Trier par</span>
          <select
            className={cn(inputCls, 'sm:w-auto')}
            value={sort}
            onChange={(e) => setSort(e.target.value as AdminUserSort)}
          >
            {ADMIN_USER_SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par rôle">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              aria-pressed={role === f.value}
              onClick={() => setRole(f.value)}
              className={cn(
                'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
                role === f.value
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-ink-200 text-ink-700 ocean:bg-white/5 bg-white',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <PendingAccountsSection />

      {users.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-ink-100 h-16 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : users.error ? (
        <p role="alert" className="text-error-600 text-sm">
          Impossible de charger les comptes.
        </p>
      ) : !users.data?.length ? (
        <p className="text-ink-500 text-sm">Aucun compte ne correspond.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((u) => (
              <li key={u.id}>
                <UserRowButton user={u} onOpen={() => setOpenUser(u)} />
              </li>
            ))}
          </ul>
          <Paginator {...paginator} />
        </>
      )}

      {openUser && <UserSheet user={openUser} onClose={() => setOpenUser(null)} />}
    </div>
  );
}

function UserRowButton({ user, onOpen }: { user: AdminUserRow; onOpen: () => void }) {
  const badges = userAccessBadges(user);
  const name = user.display_name || user.email || 'Sans nom';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="border-ink-200 hover:border-primary-300 ocean:bg-white/5 flex w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left active:scale-[0.98]"
    >
      <AvatarCircle name={name} src={user.avatar_url} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-display text-ink-900 truncate text-sm font-bold">{name}</span>
          <span className="bg-ink-100 text-ink-700 rounded-full px-2 py-0.5 text-xs font-semibold">
            {ROLE_LABELS[user.role ?? ''] ?? 'Sans rôle'}
            {user.level ? ` · ${user.level}` : ''}
          </span>
        </span>
        <span className="text-ink-500 block truncate text-xs">
          {user.email ?? '—'} · {formatLastActivity(user.last_activity_at)}
        </span>
        {badges.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {badges.map((b) => (
              <span
                key={b.label}
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  BADGE_TONES[b.tone],
                )}
              >
                {b.label}
              </span>
            ))}
          </span>
        )}
      </span>
    </button>
  );
}

function UserSheet({ user, onClose }: { user: AdminUserRow; onClose: () => void }) {
  const name = user.display_name || user.email || 'Sans nom';

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Compte de ${name}`}
        onClick={(e) => e.stopPropagation()}
        className="ocean:bg-[#061433] flex h-full w-full flex-col bg-white sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-3xl"
      >
        <div className="border-ink-100 native:pt-[max(1rem,env(safe-area-inset-top))] flex items-center gap-3 border-b p-4">
          <AvatarCircle name={name} src={user.avatar_url} size="md" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-ink-900 truncate text-lg font-bold">{name}</p>
            <p className="text-ink-500 truncate text-xs">{user.email ?? '—'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-500 hover:text-ink-900 flex h-11 w-11 items-center justify-center rounded-full"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="flex-1 space-y-6 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <Info label="Rôle">
              {ROLE_LABELS[user.role ?? ''] ?? 'Sans rôle'}
              {user.level ? ` · ${user.level}` : ''}
            </Info>
            <Info label="Inscrit le">{formatDateTime(user.created_at)}</Info>
            <Info label="Dernière activité">
              {formatDateTime(user.last_activity_at)}
              <span className="text-ink-500 block text-xs">
                Connexion, app ouverte ou exercice joué
              </span>
            </Info>
            <Info label="Dernière connexion">{formatDateTime(user.last_sign_in_at)}</Info>
            <Info label="Identifiant">
              <span className="break-all font-mono text-xs">{user.id}</span>
            </Info>
          </dl>
          <SwitchToAccountSection user={user} />
          <UserPermissionsSection userId={user.id} role={user.role} name={user.display_name} />
          <UserActivitySection userId={user.id} />
          <UserAccessSection user={user} />
          <GiftForm user={user} />
          <DeleteUserSection user={user} onDeleted={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-ink-50 ocean:bg-white/5 rounded-xl p-3">
      <dt className={labelCls}>{label}</dt>
      <dd className="text-ink-900 mt-0.5">{children}</dd>
    </div>
  );
}

function UserAccessSection({ user }: { user: AdminUserRow }) {
  const access = useAdminUserAccess(user.id);
  const revoke = useRevokeGift();
  const [error, setError] = useState<string | null>(null);
  const now = new Date();

  if (access.isLoading) return <div className="bg-ink-100 h-24 animate-pulse rounded-2xl" />;
  if (access.error || !access.data) {
    return (
      <p role="alert" className="text-error-600 text-sm">
        Impossible de charger les accès de ce compte.
      </p>
    );
  }
  const { passes, subscriptions, grants, deck_expires_at } = access.data;
  const activePasses = passes.filter((p) => new Date(p.expires_at) > now);

  async function onRevoke(id: string) {
    if (!window.confirm('Retirer cet accès offert ? Il prend fin immédiatement.')) return;
    setError(null);
    try {
      await revoke.mutateAsync({ accessRightId: id, userId: user.id });
    } catch (e) {
      setError(adminAccessErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="font-display text-ink-900 text-base font-bold">Accès actuels</h2>
      {subscriptions.length === 0 && activePasses.length === 0 && !deck_expires_at && (
        <p className="text-ink-500 text-sm">Aucun accès payant ou offert en cours.</p>
      )}
      <ul className="space-y-2">
        {subscriptions.map((s) => (
          <li key={s.id} className="border-ink-200 rounded-xl border p-3 text-sm">
            <p className="text-ink-900 font-semibold">
              Abonnement {s.plan} · {s.status}
              {s.is_owner ? '' : ' (siège)'}
            </p>
            <p className="text-ink-500 text-xs">
              {s.provider ?? 'stripe'} · jusqu’au {formatDateTime(s.current_period_end)}
            </p>
          </li>
        ))}
        {activePasses.map((p) => (
          <li
            key={p.id}
            className="border-ink-200 flex flex-col gap-2 rounded-xl border p-3 text-sm sm:flex-row sm:items-center"
          >
            <div className="flex-1">
              <p className="text-ink-900 font-semibold">
                {p.type === 'epreuves' ? 'Pass Épreuves' : 'Accès complet'} ·{' '}
                {PASS_SOURCE_LABELS[p.source] ?? p.source}
              </p>
              <p className="text-ink-500 text-xs">jusqu’au {formatDateTime(p.expires_at)}</p>
            </div>
            {p.source === 'admin_gift' && (
              <Button
                variant="outline"
                size="sm"
                className="min-h-[2.75rem] w-full sm:w-auto"
                disabled={revoke.isPending}
                onClick={() => void onRevoke(p.id)}
              >
                Retirer
              </Button>
            )}
          </li>
        ))}
        {deck_expires_at && new Date(deck_expires_at) > now && (
          <li className="border-ink-200 rounded-xl border p-3 text-sm">
            <p className="text-ink-900 font-semibold">Deck de SKILLCARDS achetées</p>
            <p className="text-ink-500 text-xs">jusqu’au {formatDateTime(deck_expires_at)}</p>
          </li>
        )}
      </ul>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}

      {grants.length > 0 && (
        <details className="border-ink-200 rounded-xl border p-3">
          <summary className="text-ink-900 min-h-[2.75rem] cursor-pointer content-center text-sm font-semibold">
            Historique des accès offerts ({grants.length})
          </summary>
          <ul className="mt-2 space-y-3">
            {grants.map((g) => (
              <li key={g.id} className="text-sm">
                <p className="text-ink-900 font-semibold">
                  {g.access_type === 'epreuves' ? 'Pass Épreuves' : 'Accès complet'} ·{' '}
                  {giftDurationLabel(g.duration_days)}
                  {g.revoked_at ? ' · retiré' : ''}
                </p>
                <p className="text-ink-500 text-xs">
                  Le {formatDateTime(g.created_at)}
                  {g.granted_by_name ? ` par ${g.granted_by_name}` : ''} · jusqu’au{' '}
                  {formatDateTime(g.expires_at)} ·{' '}
                  {g.push_sent || g.email_sent
                    ? [g.push_sent && 'push', g.email_sent && 'email'].filter(Boolean).join(' + ')
                    : 'non notifié'}
                </p>
                {g.message_body && (
                  <p className="text-ink-600 bg-ink-50 ocean:bg-white/5 mt-1 whitespace-pre-line rounded-lg p-2 text-xs">
                    {g.message_body}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function GiftForm({ user }: { user: AdminUserRow }) {
  const grant = useGrantAccess();
  const [typesKey, setTypesKey] = useState(0);
  const [days, setDays] = useState('30');
  const [extend, setExtend] = useState(true);
  const [push, setPush] = useState(true);
  const [email, setEmail] = useState(true);
  const [title, setTitle] = useState(DEFAULT_GIFT_TITLE);
  const [template, setTemplate] = useState(DEFAULT_GIFT_TEMPLATE);
  const [result, setResult] = useState<GrantAccessResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const types: GiftAccessType[] = GIFT_ACCESS_OPTIONS[typesKey]!.value;
  const durationDays = parseGiftDays(days);
  const notify = push || email;

  const preview = useMemo(() => {
    if (!durationDays) return null;
    const end = giftEndDate(
      types,
      { normal: user.normal_until, epreuves: user.epreuves_until },
      new Date(),
      durationDays,
      extend,
    );
    return {
      end,
      text: renderGiftMessage(template, {
        prenom: giftFirstName(user.display_name),
        acces: giftAccessLabel(types),
        duree: giftDurationLabel(durationDays),
        date_fin: formatGiftDate(end),
      }),
    };
  }, [types, durationDays, extend, template, user]);

  const titleOk = title.trim().length >= 1 && title.length <= GIFT_TITLE_MAX;
  const bodyOk = template.trim().length >= 1 && template.length <= GIFT_BODY_MAX;
  const canSubmit = !!durationDays && (!notify || (titleOk && bodyOk)) && !grant.isPending;

  async function onSubmit() {
    if (!durationDays) return;
    setError(null);
    setResult(null);
    try {
      const r = await grant.mutateAsync({
        userId: user.id,
        accessTypes: types,
        durationDays,
        extend,
        notify: { push, email, title: title.trim(), template: template.trim() },
      });
      setResult(r);
    } catch (e) {
      setError(adminAccessErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  return (
    <section className="border-primary-200 bg-primary-50/40 ocean:bg-white/5 space-y-4 rounded-2xl border p-4">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-base font-bold">
        <Gift className="h-5 w-5" aria-hidden /> Offrir un accès
      </h2>

      <fieldset className="space-y-2">
        <legend className={labelCls}>Accès</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {GIFT_ACCESS_OPTIONS.map((o, i) => (
            <button
              key={o.label}
              type="button"
              aria-pressed={typesKey === i}
              onClick={() => setTypesKey(i)}
              className={cn(
                'min-h-[2.75rem] rounded-xl border p-3 text-left active:scale-[0.98]',
                typesKey === i
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-ink-200 text-ink-900 ocean:bg-white/5 bg-white',
              )}
            >
              <span className="block text-sm font-semibold">{o.label}</span>
              <span
                className={cn('block text-xs', typesKey === i ? 'text-white/80' : 'text-ink-500')}
              >
                {o.hint}
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className={labelCls}>Durée</legend>
        <div className="flex flex-wrap gap-2">
          {GIFT_DURATION_PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              aria-pressed={days === String(p.days)}
              onClick={() => setDays(String(p.days))}
              className={cn(
                'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
                days === String(p.days)
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-ink-200 text-ink-700 ocean:bg-white/5 bg-white',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-ink-700 shrink-0">Ou nombre de jours</span>
          <input
            className={cn(inputCls, 'w-24')}
            inputMode="numeric"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            aria-invalid={!durationDays}
          />
        </label>
        {!durationDays && <p className="text-error-600 text-xs">Entre 1 et 3650 jours.</p>}
        <label className="flex min-h-[2.75rem] items-center gap-2 text-sm">
          <input type="checkbox" checked={extend} onChange={(e) => setExtend(e.target.checked)} />
          <span className="text-ink-700">
            Prolonger un pass déjà actif (sinon : à partir d’aujourd’hui)
          </span>
        </label>
        {preview && (
          <p className="text-ink-600 text-xs">
            Fin de l’accès : <strong>{formatGiftDate(preview.end)}</strong>
          </p>
        )}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className={labelCls}>Notification</legend>
        <div className="flex flex-wrap gap-4">
          <label className="flex min-h-[2.75rem] items-center gap-2 text-sm">
            <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} />
            <span className="text-ink-700">Notification push</span>
          </label>
          <label className="flex min-h-[2.75rem] items-center gap-2 text-sm">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
            <span className="text-ink-700">Email</span>
          </label>
        </div>
        {notify && (
          <>
            <label className="block space-y-1">
              <span className={labelCls}>Titre</span>
              <input
                className={inputCls}
                maxLength={GIFT_TITLE_MAX}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="block space-y-1">
              <span className={labelCls}>Message</span>
              <textarea
                className={cn(inputCls, 'min-h-[8rem] py-2')}
                maxLength={GIFT_BODY_MAX}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              />
              <span className="text-ink-500 block text-xs">
                Variables remplacées à l’envoi : {'{prenom}'}, {'{acces}'}, {'{duree}'},{' '}
                {'{date_fin}'}. {template.length}/{GIFT_BODY_MAX}
              </span>
            </label>
            <button
              type="button"
              className="text-primary-700 min-h-[2.75rem] text-sm font-semibold underline"
              onClick={() => {
                setTitle(DEFAULT_GIFT_TITLE);
                setTemplate(DEFAULT_GIFT_TEMPLATE);
              }}
            >
              Rétablir le message par défaut
            </button>
            {preview && (
              <div className="border-ink-200 ocean:bg-white/5 rounded-xl border bg-white p-3">
                <p className={labelCls}>Aperçu</p>
                <p className="text-ink-900 mt-1 text-sm font-semibold">{title}</p>
                <p className="text-ink-700 mt-1 whitespace-pre-line text-sm">{preview.text}</p>
              </div>
            )}
          </>
        )}
      </fieldset>

      <Button
        className="min-h-[2.75rem] w-full sm:w-auto"
        disabled={!canSubmit}
        onClick={() => void onSubmit()}
      >
        {grant.isPending ? 'Envoi…' : notify ? 'Offrir et notifier' : 'Offrir sans notifier'}
      </Button>

      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
      {result && (
        <p role="status" className="bg-success-50 text-success-800 rounded-xl p-3 text-sm">
          Accès offert jusqu’au {formatGiftDate(new Date(result.expires_at))}.{' '}
          {giftNotifySummary(result, push, email)}
        </p>
      )}
    </section>
  );
}

/**
 * Compte interne (`@jeraisonne.fr` ou admin) : l'admin s'y connecte en un clic
 * (edge fn `admin-switch-account`) et revient par « Changer de compte ».
 * Rien n'est affiché pour un vrai compte.
 */
export function SwitchToAccountSection({ user }: { user: AdminUserRow }) {
  const { user: me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = adminSwitchBlockReason({
    email: user.email,
    role: user.role,
    targetId: user.id,
    actorId: me?.id ?? '',
  });
  if (reason) return null;

  async function onSwitch() {
    setError(null);
    setBusy(true);
    try {
      await switchToInternalAccount(user.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La connexion à ce compte a échoué.');
      setBusy(false);
    }
  }

  return (
    <section className="border-primary-200 space-y-3 rounded-2xl border p-4">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-base font-bold">
        <LogIn className="h-5 w-5" aria-hidden /> Se connecter à ce compte
      </h2>
      <p className="text-ink-600 text-sm">
        Compte interne : tu vois l’app comme ce compte, sans code. Reviens ensuite avec le bouton «
        Revenir admin » en bas de l’écran.
      </p>
      <Button
        className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        disabled={busy}
        onClick={() => void onSwitch()}
      >
        {busy ? 'Connexion…' : 'Me connecter à ce compte'}
      </Button>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteUserSection({ user, onDeleted }: { user: AdminUserRow; onDeleted: () => void }) {
  const { user: me } = useAuth();
  const del = useDeleteUser();
  const [error, setError] = useState<string | null>(null);
  const reason = deleteBlockReason({
    email: user.email,
    role: user.role,
    targetId: user.id,
    actorId: me?.id ?? '',
  });
  // Un vrai compte : on n'affiche rien (pas de bruit sur la fiche).
  if (reason === 'not_test_account') return null;
  const name = user.display_name || user.email || 'ce compte';

  async function onDelete() {
    if (
      !window.confirm(
        `Supprimer définitivement le compte de ${name} (${user.email}) ?\n\nSes progrès, messages, liens et accès seront effacés. Cette action est irréversible.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await del.mutateAsync(user.id);
      onDeleted();
    } catch (e) {
      setError(adminAccessErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  return (
    <section className="border-error-200 space-y-3 rounded-2xl border p-4">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-base font-bold">
        <Trash2 className="h-5 w-5" aria-hidden /> Supprimer ce compte
      </h2>
      {reason ? (
        <p className="text-ink-500 text-sm">{deleteBlockLabel(reason)}</p>
      ) : (
        <>
          <p className="text-ink-600 text-sm">
            Compte interne ou de test : suppression définitive avec toutes ses données.
          </p>
          <Button
            variant="outline"
            className="border-error-300 text-error-700 min-h-[2.75rem] w-full sm:w-auto"
            disabled={del.isPending}
            onClick={() => void onDelete()}
          >
            {del.isPending ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </>
      )}
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}

/** « Push envoyé. Email non envoyé (aucun appareil)… » */
export function giftNotifySummary(r: GrantAccessResult, push: boolean, email: boolean): string {
  const parts: string[] = [];
  if (push) {
    parts.push(
      r.push_sent
        ? 'Notification push envoyée.'
        : r.push_skipped === 'no_device'
          ? 'Pas de push : aucun appareil enregistré.'
          : r.push_skipped === 'push_refused'
            ? 'Pas de push : ses appareils ont refusé la notification (jeton périmé).'
            : 'La notification push a échoué.',
    );
  }
  if (email) {
    parts.push(
      r.email_sent
        ? 'Email envoyé.'
        : r.email_skipped === 'no_email'
          ? 'Pas d’email : compte sans adresse.'
          : r.email_skipped === 'smtp_not_configured'
            ? 'Pas d’email : envoi non configuré.'
            : 'L’email a échoué.',
    );
  }
  return parts.join(' ');
}
